import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import Button from '../ui/Button';
import soilService from '../../services/soil';
import { errorMessage } from '../../utils/apiResponse';
import { LEAF_ANALYTES } from '../../utils/soilAnalytes';
import type { CropRemoval, LeafRanges, LeafStandardsResearch } from '../../types/soil';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-500';

type Draft = Record<string, { low: string; high: string }>;

const toDraft = (r: LeafRanges): Draft => Object.fromEntries(LEAF_ANALYTES.map((a) => [a.key, {
  low: r[a.key]?.low != null ? String(r[a.key].low) : '',
  high: r[a.key]?.high != null ? String(r[a.key].high) : '',
}]));

const numOrNull = (s: string) => (s.trim() === '' ? null : Number(s));

/**
 * A crop's leaf standards: the adequate range for each leaf analyte, which a
 * leaf sample from a planting of that crop is marked against. The
 * organisation's own figures, typed or looked up (AI with web search, a
 * proposal with its sources and the sampling it assumes, saved only on Save).
 */
export default function LeafStandardsEditor({ crop, onSaved }: { crop: CropRemoval; onSaved: (c: CropRemoval) => void }) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(crop.leaf_ranges ?? {}));
  const [source, setSource] = useState(crop.leaf_source ?? '');
  const [research, setResearch] = useState<LeafStandardsResearch | null>(null);
  const [busy, setBusy] = useState<'save' | 'research' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const set = (key: string, end: 'low' | 'high', v: string) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], [end]: v } }));

  const runResearch = async () => {
    setBusy('research');
    setError(null);
    try {
      const res = await soilService.researchLeafStandards(crop.crop_type_id);
      if (!res.success || !res.data) throw res;
      setResearch(res.data);
      if (res.data.found) {
        setDraft(toDraft(res.data.ranges));
        setSource(['Researched by AI and reviewed.', res.data.basis, res.data.sources.map((s) => s.url).join(' ')]
          .filter(Boolean).join(' ').slice(0, 2000));
      }
    } catch (e) {
      setError(errorMessage(e, 'The research did not finish'));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    const ranges: LeafRanges = {};
    const bad: string[] = [];
    for (const a of LEAF_ANALYTES) {
      const low = numOrNull(draft[a.key].low);
      const high = numOrNull(draft[a.key].high);
      if ((low != null && !(low >= 0)) || (high != null && !(high >= 0)) || (low != null && high != null && low > high)) {
        bad.push(a.label);
        continue;
      }
      if (low != null || high != null) ranges[a.key] = { low, high };
    }
    if (bad.length) { setError(`Check ${bad.join(', ')}: a number, with the low end not above the high end.`); return; }
    setBusy('save');
    setError(null);
    setSaved(false);
    try {
      const res = await soilService.setLeafStandards(crop.crop_type_id, ranges, source.trim() || null);
      if (!res.success || !res.data) throw res;
      onSaved(res.data);
      setResearch(null);
      setSaved(true);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the standards'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-neutral-800">Leaf standards</h4>
          <p className="text-xs text-neutral-500">The adequate range in leaf tissue, dry weight. A leaf sample from this crop is marked against it.</p>
        </div>
        <Button size="sm" variant="outline" isLoading={busy === 'research'} disabled={busy !== null} onClick={runResearch}>
          <Sparkles className="h-4 w-4" />
          Look up leaf standards
        </Button>
      </div>

      {busy === 'research' && <p className="text-sm text-neutral-600">Searching published sources. This can take a couple of minutes.</p>}

      {research && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 space-y-2 text-sm">
          <p className="text-primary-900">
            {research.found
              ? 'Researched by AI: check each range against its source, and sample leaves the way it assumes. Nothing is saved until you press Save.'
              : 'Nothing usable was found. Type the ranges from your own source.'}
          </p>
          {research.basis && <p className="text-neutral-700"><strong>Sampling:</strong> {research.basis}</p>}
          {research.notes && <p className="text-neutral-700">{research.notes}</p>}
          {research.warnings.length > 0 && (
            <ul className="list-disc pl-5 text-amber-800 text-xs space-y-0.5">{research.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          )}
          {research.sources.length > 0 && (
            <ul className="text-xs space-y-0.5">
              {research.sources.map((s) => (
                <li key={s.url} className="break-all">
                  <a href={s.url} target="_blank" rel="noreferrer" className="text-primary-700 hover:underline">{s.title}</a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {LEAF_ANALYTES.map((a) => (
          <div key={a.key} className="grid grid-cols-[minmax(0,1fr)_4.5rem_4.5rem] items-center gap-2">
            <span className="text-sm text-neutral-700 min-w-0 break-words">{a.label} <span className="text-neutral-500">({a.unit})</span></span>
            <input type="text" inputMode="decimal" aria-label={`${a.label} low`} className={inputCls}
              value={draft[a.key].low} onChange={(e) => set(a.key, 'low', e.target.value)} placeholder="low" />
            <input type="text" inputMode="decimal" aria-label={`${a.label} high`} className={inputCls}
              value={draft[a.key].high} onChange={(e) => set(a.key, 'high', e.target.value)} placeholder="high" />
          </div>
        ))}
      </div>

      <label className="block text-sm">
        <span className="text-neutral-700">Where the standards come from, and how leaves are sampled for them</span>
        <textarea className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" rows={2} value={source}
          onChange={(e) => setSource(e.target.value)} />
      </label>

      {error && <p className="text-sm text-error-700">{error}</p>}
      {saved && <p className="text-sm text-green-700">Saved.</p>}
      <div className="flex justify-end">
        <Button variant="primary" size="sm" isLoading={busy === 'save'} disabled={busy !== null} onClick={save}>Save leaf standards</Button>
      </div>
    </div>
  );
}
